import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { cn } from "@/lib/utils";

export const SOURCE_ACCEPT =
  ".pdf,.docx,.pptx,.html,.md,.csv,.xlsx";

export const MAX_MANUAL_SOURCE_FILES = 10;

const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".pptx", ".html", ".md", ".csv", ".xlsx"];

export function isAllowedSourceFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function getSourceFileType(name: string): string {
  const idx = name.lastIndexOf(".");
  if (idx < 0) return "file";
  return name.slice(idx + 1).toLowerCase();
}

export interface SourcesDropzoneHandle {
  openFilePicker: () => void;
}

interface SourcesDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  onInvalidFiles?: () => void;
  onFileLimitExceeded?: () => void;
  disabled?: boolean;
  className?: string;
}

export const SourcesDropzone = forwardRef<SourcesDropzoneHandle, SourcesDropzoneProps>(
  function SourcesDropzone(
    {
      onFilesSelected,
      onInvalidFiles,
      onFileLimitExceeded,
      disabled = false,
      className,
    },
    ref
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    useImperativeHandle(ref, () => ({
      openFilePicker: () => {
        if (!disabled) {
          inputRef.current?.click();
        }
      },
    }));

    const processFiles = useCallback(
      (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;

        const valid: File[] = [];
        let hasInvalid = false;

        for (const file of Array.from(fileList)) {
          if (isAllowedSourceFile(file)) {
            valid.push(file);
          } else {
            hasInvalid = true;
          }
        }

        if (hasInvalid) {
          onInvalidFiles?.();
        }

        if (valid.length === 0) return;

        if (valid.length > MAX_MANUAL_SOURCE_FILES) {
          onFilesSelected(valid.slice(0, MAX_MANUAL_SOURCE_FILES));
          onFileLimitExceeded?.();
          return;
        }

        onFilesSelected(valid);
      },
      [onFilesSelected, onInvalidFiles, onFileLimitExceeded]
    );

    const handleDragOver = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault();
        if (!disabled) {
          setIsDragging(true);
        }
      },
      [disabled]
    );

    const handleDragLeave = useCallback((event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
    }, []);

    const handleDrop = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        processFiles(event.dataTransfer.files);
      },
      [disabled, processFiles]
    );

    const handleFileInput = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        processFiles(event.target.files);
        event.target.value = "";
      },
      [processFiles]
    );

    const handleBrowseClick = useCallback(() => {
      if (!disabled) {
        inputRef.current?.click();
      }
    }, [disabled]);

    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleBrowseClick();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        className={cn(
          "relative min-h-[160px] cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50",
          disabled && "cursor-not-allowed opacity-60",
          isDragging
            ? "border-[var(--dropzone-border)] bg-[var(--dropzone-bg)]"
            : "border-[var(--dropzone-border)] bg-[var(--dropzone-bg)] hover:border-[rgba(147,51,245,0.4)] hover:bg-[rgba(147,51,245,0.06)] dark:border-white/15 dark:bg-slate-950/25 dark:hover:border-violet-500/35 dark:hover:bg-slate-900/40",
          className
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={SOURCE_ACCEPT}
          multiple
          disabled={disabled}
          onChange={handleFileInput}
          className="hidden"
          tabIndex={-1}
          aria-hidden="true"
        />
        <div className="pointer-events-none flex min-h-[160px] flex-col items-center justify-center px-6 py-8 text-center">
          <div
            className={cn(
              "mb-3 flex h-14 w-14 items-center justify-center rounded-full transition-colors",
              isDragging ? "bg-[var(--primary-soft-bg)] text-[#7c3aed] dark:bg-violet-500/20 dark:text-violet-300" : "bg-[var(--surface-sunken)] text-[#576274] dark:bg-slate-800/80 dark:text-slate-400"
            )}
          >
            <Upload className="h-7 w-7" />
          </div>
          <p className="text-[13px] font-medium text-slate-200">
            Drop files here or click to browse
          </p>
          <p className="mt-1 text-[11px] text-slate-500">
            PDF, DOCX, PPTX, HTML, MD, CSV, XLSX
          </p>
          <p className="mt-1 text-[11px] text-slate-500">
            Up to {MAX_MANUAL_SOURCE_FILES} files at a time
          </p>
        </div>
      </div>
    );
  }
);
